import { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { settingsService } from '../services/settingsService';
import { useLanguage } from '../contexts/LanguageContext';

export default function Login() {
  const { signIn, loading, user } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load stored branding from localStorage FIRST (instant display, no flash)
  // These values are saved when user updates settings in the app
  const [storeName, setStoreName] = useState(() => {
    return localStorage.getItem('storeName') || '';
  });
  const [logoUrl, setLogoUrl] = useState(() => {
    return localStorage.getItem('logoUrl') || '';
  });
  const [primaryColor, setPrimaryColor] = useState(() => {
    return localStorage.getItem('primaryColor') || 'indigo';
  });

  // Optionally fetch fresh settings from Supabase in background
  // This updates localStorage if settings changed in database
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const generalSettings = await settingsService.getGeneralSettings();
        const uiSettings = await settingsService.getUISettings();

        console.log('[Login] Fetched settings from Supabase:', { generalSettings, uiSettings });

        // Update localStorage and state if values changed
        if (generalSettings.storeName && generalSettings.storeName !== storeName) {
          setStoreName(generalSettings.storeName);
          localStorage.setItem('storeName', generalSettings.storeName);
        }
        if (uiSettings?.logoUrl && uiSettings.logoUrl !== logoUrl) {
          setLogoUrl(uiSettings.logoUrl);
          localStorage.setItem('logoUrl', uiSettings.logoUrl);
        }
        if (uiSettings?.primaryColor && uiSettings.primaryColor !== primaryColor) {
          setPrimaryColor(uiSettings.primaryColor);
          localStorage.setItem('primaryColor', uiSettings.primaryColor);
        }
      } catch (error) {
        console.error('[Login] Error fetching settings:', error);
        // Keep stored values from localStorage if fetch fails
      }
    };

    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill email if previously saved (Remember Me convenience — password is NOT stored)
  useEffect(() => {
    const savedEmail = localStorage.getItem('crm_saved_email');
    const wasRemembered = localStorage.getItem('crm_remember_me') === '1';

    if (savedEmail && wasRemembered) {
      console.log('[Login] Found saved email, pre-filling form...');
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);

    try {
      // Save email for convenience pre-fill on next visit (if Remember Me is checked)
      if (rememberMe) {
        localStorage.setItem('crm_saved_email', email);
      } else {
        localStorage.removeItem('crm_saved_email');
        localStorage.removeItem('crm_remember_me');
      }

      // Pass rememberMe to signIn — AuthProvider will enable session persistence
      await signIn(email, password, rememberMe);

      // No need to navigate - App.js will automatically show dashboard when user is set
    } catch (e) {
      setErr(e?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };


  // Color mapping for dynamic theming
  const colorMap = {
    indigo: { bg: '#4F46E5', hover: '#4338CA', border: '#3730A3', ring: '#6366F1' },
    blue: { bg: '#2563EB', hover: '#1D4ED8', border: '#1E3A8A', ring: '#3B82F6' },
    green: { bg: '#059669', hover: '#047857', border: '#064E3B', ring: '#10B981' },
    purple: { bg: '#7C3AED', hover: '#6D28D9', border: '#5B21B6', ring: '#8B5CF6' },
    pink: { bg: '#DB2777', hover: '#BE185D', border: '#9F1239', ring: '#EC4899' },
    orange: { bg: '#EA580C', hover: '#C2410C', border: '#9A3412', ring: '#F97316' },
    red: { bg: '#DC2626', hover: '#B91C1C', border: '#991B1B', ring: '#EF4444' },
    yellow: { bg: '#CA8A04', hover: '#A16207', border: '#854D0E', ring: '#EAB308' },
    violet: { bg: '#440089', hover: '#38006E', border: '#2C0056', ring: '#640AFF' },
    teal: { bg: '#0D9488', hover: '#0F766E', border: '#115E59', ring: '#14B8A6' },
    cyan: { bg: '#0891B2', hover: '#0E7490', border: '#155E75', ring: '#06B6D4' },
    amber: { bg: '#D97706', hover: '#B45309', border: '#92400E', ring: '#F59E0B' },
    lime: { bg: '#65A30D', hover: '#4D7C0F', border: '#3F6212', ring: '#84CC16' },
    fuchsia: { bg: '#C026D3', hover: '#A21CAF', border: '#86198F', ring: '#D946EF' },
    rose: { bg: '#E11D48', hover: '#BE123C', border: '#9F1239', ring: '#F43F5E' },
  };

  const colors = colorMap[primaryColor] || colorMap.indigo;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-100 p-4"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F3F4F6',
        padding: '1rem'
      }}
    >
      <div
        className="bg-white p-6 md:p-8 rounded-lg shadow-md w-full max-w-md"
        style={{
          minHeight: '400px',
          backgroundColor: '#FFFFFF',
          padding: '2rem',
          borderRadius: '0.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          width: '100%',
          maxWidth: '28rem'
        }}
      >
        {/* Logo and Store Name */}
        <div
          className="flex flex-col items-center mb-6"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}
        >
          {logoUrl && (
            <div
              className="mb-3 flex items-center justify-center overflow-hidden flex-shrink-0 bg-gray-50"
              style={{
                width: '80px',
                height: '80px',
                minWidth: '80px',
                minHeight: '80px',
                maxWidth: '80px',
                maxHeight: '80px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: '#F9FAFB',
                borderRadius: '0.5rem',
                marginBottom: '0.75rem'
              }}
            >
              <img
                src={logoUrl}
                alt={`${storeName} Logo`}
                className="rounded-lg"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  display: imageLoaded ? 'block' : 'none'
                }}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.target.style.display = 'none';
                  setImageLoaded(false);
                }}
                loading="eager"
              />
              {!imageLoaded && (
                <div
                  className="w-20 h-20 flex items-center justify-center text-gray-400"
                  style={{
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9CA3AF'
                  }}
                >
                  <svg
                    className="w-12 h-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ width: '48px', height: '48px' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
          )}
          <h1
            className="text-xl md:text-2xl font-bold text-center"
            style={{
              color: colors.bg,
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginTop: '0'
            }}
          >
            {storeName ? `${storeName} ${t('login')}` : t('login')}
          </h1>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}
            >
              {t('email')}
            </label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none"
              style={{
                marginTop: '0.25rem',
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                fontSize: '1rem',
                lineHeight: '1.5rem'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.ring;
                e.target.style.boxShadow = `0 0 0 1px ${colors.ring}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
              }}
              required
              disabled={submitting || loading}
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}
            >
              {t('password')}
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none"
              style={{
                marginTop: '0.25rem',
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                fontSize: '1rem',
                lineHeight: '1.5rem'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.ring;
                e.target.style.boxShadow = `0 0 0 1px ${colors.ring}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
              }}
              required
              disabled={submitting || loading}
            />
          </div>

          <div
            className="flex items-center"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              className="h-4 w-4 border-gray-300 rounded"
              style={{
                accentColor: colors.bg,
                width: '1rem',
                height: '1rem',
                cursor: 'pointer'
              }}
              disabled={submitting || loading}
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-900"
              style={{ marginLeft: '0.5rem', display: 'block', fontSize: '0.875rem', color: '#111827', cursor: 'pointer' }}
            >
              {t('rememberMe')}
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || loading}
            className="w-full text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 transition-colors"
            style={{
              width: '100%',
              color: '#FFFFFF',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              backgroundColor: colors.bg,
              border: 'none',
              cursor: submitting || loading ? 'not-allowed' : 'pointer',
              opacity: submitting || loading ? 0.6 : 1,
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!submitting && !loading) {
                e.target.style.backgroundColor = colors.hover;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.bg;
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = `0 0 0 2px ${colors.ring}`;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
            }}
          >
            {submitting ? t('loggingIn') : t('login')}
          </button>

          {err && (
            <p
              className="text-red-500 text-sm text-center"
              style={{ color: '#EF4444', fontSize: '0.875rem', textAlign: 'center', marginTop: '0.5rem' }}
            >
              {err}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
